import {
  defineComponent, ref, reactive, watch, computed, onMounted, nextTick, PropType,
} from 'vue'
import {
  ObjectFit, PaletteColor, AvatarSize, AvatarShape,
} from '../../enums'
import { MouseState } from '../../states'

export default defineComponent({
  props: {
    /**
     * 头像尺寸
     */
    size: {
      type: String as PropType<AvatarSize>,
      default: 'default',
    },
    /**
     * 头像变体
     */
    shape: {
      type: String as PropType<AvatarShape>,
      default: 'circle',
    },
    /**
     * 背景颜色
     */
    backgroundColor: {
      type: String as PropType<PaletteColor>,
      default: 'grey-4',
    },
    /**
     * 图片
     */
    src: {
      type: String,
      default: undefined,
    },
    /**
     * 图片如何适配头像区域的尺寸
     */
    objectFit: {
      type: String as PropType<ObjectFit>,
      default: 'cover',
    },
    /**
     * 图片加载失败的回调，可通过返回 false 取消默认 fallback 行为
     */
    onError: {
      type: Function as PropType<(e: Event) => boolean | Promise<boolean>>,
      default: undefined,
    },
    /**
     * 文本
     */
    text: {
      type: String,
      default: undefined,
    },
    /**
     * 文本与左右边界的距离
     */
    gap: {
      type: Number,
      default: 4,
    },
    /**
     * 文本是否截断
     */
    clip: {
      type: Boolean,
      default: true,
    },
    /**
     * 文本颜色
     */
    color: {
      type: String as PropType<PaletteColor>,
      default: 'white',
    },
    /**
     * 头像对应名称
     */
    name: {
      type: String,
      default: undefined,
    },
  },
  emits: {
    click(e: MouseEvent) {
      return e !== undefined
    },
    mousedown(e: MouseEvent) {
      return e !== undefined
    },
    mouseenter(e: MouseEvent) {
      return e !== undefined
    },
    mouseleave(e: MouseEvent) {
      return e !== undefined
    },
    mouseup(e: MouseEvent) {
      return e !== undefined
    },
    'state-change'(state: MouseState) {
      return state !== undefined
    },
  },
  setup(props, { slots, emit }) {
    const state = reactive({
      pressing: false,
      hover: false,
    })

    watch(
      state,
      v => emit('state-change', v),
    )

    const avatar = ref<HTMLElement>()

    const imageLoadError = ref(false)
    const imageFallback = ref(true)

    const useImg = computed(
      () => props.src && (!imageLoadError.value || !imageFallback.value),
    )

    watch(
      () => props.src,
      () => {
        imageLoadError.value = false
        imageFallback.value = true
      },
    )

    const useText = computed(
      () => !useImg.value
      && !slots.default
      && props.text !== undefined,
    )

    const textGap = computed(
      () => (props.gap > 4 ? props.gap : 4),
    )

    const maxTextSize = computed(
      () => {
        switch (props.size) {
          case 'extra-large':
          case 'large':
            return 32
          case 'small':
            return 14
          case 'extra-small':
            return 12
          case 'default':
          default:
            return 20
        }
      },
    )
    const fontSize = ref(maxTextSize.value)
    const displayText = ref(props.text)
    const drawback = ref(false)

    function adjustText() {
      if (avatar.value.clientWidth < avatar.value.scrollWidth) {
        if (fontSize.value > 12) {
          fontSize.value -= 1
          drawback.value = false

          nextTick(
            () => adjustText(),
          )
        } else if (props.clip) {
          displayText.value = displayText.value.slice(0, -1)
          drawback.value = true

          nextTick(
            () => adjustText(),
          )
        }
      } else if (drawback.value) {
        if (fontSize.value < maxTextSize.value) {
          fontSize.value += 1

          nextTick(
            () => adjustText(),
          )
        }
      }
    }

    onMounted(
      () => {
        watch(
          [
            useText,
            maxTextSize,
            () => props.text,
          ],
          ([use, maxSize, v]) => {
            fontSize.value = maxSize
            displayText.value = v

            if (use) {
              nextTick(
                () => adjustText(),
              )
            }
          },
          { immediate: true },
        )
      },
    )

    return () => <span
      ref={avatar}
      class={
        [
          'd-avatar',
          `d-avatar-${props.size}`,
          `d-avatar-${props.shape}`,
          `--color-${props.color}`,
          `--color-bg-${props.backgroundColor}`,
        ]
      }
      style={
        useText.value
          ? {
            paddingRight: `${textGap.value}px`,
            paddingLeft: `${textGap.value}px`,
            fontSize: `${fontSize.value}px`,
          }
          : undefined
      }
      {
        ...{

          onClick: (e: MouseEvent) => emit('click', e),
          onMousedown: (e: MouseEvent) => {
            emit('mousedown', e)
            state.pressing = true
          },
          onMouseenter: (e: MouseEvent) => {
            emit('mouseenter', e)
            state.hover = true
          },
          onMouseleave: (e: MouseEvent) => {
            emit('mouseleave', e)
            state.hover = false
            state.pressing = false
          },
          onMouseup: (e: MouseEvent) => {
            emit('mouseup', e)
            state.pressing = false
          },
        }
      }
    >
      {
        useImg.value
          ? <img
            src={props.src}
            style={{ objectFit: props.objectFit }}
            onError={
              e => {
                Promise
                  .resolve(props.onError?.(e) ?? true)
                  .then(
                    res => {
                      imageLoadError.value = true
                      imageFallback.value = !!res
                    },
                  )
              }
            }
          />
          : (
            slots.default?.()
            || <span class={'d-avatar-text'}>{displayText.value}</span>
          )
      }
    </span>
  },
})
